import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { CarteraDashboardGroupItem } from "@bitacorapro/api-types";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/icons";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { ProjCard } from "../components/ProjCard";
import { Skel } from "../components/Skel";
import { carteraDashboardApi } from "../lib/api/endpoints/cartera-dashboard";
import { useCarteraProyectos, useRecipe } from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { useQueryView } from "../lib/loading/useQueryView";
import { canUsePortfolioAnalysis } from "../lib/nav";
import { fmtDate, lastSeenLabel, sevLabel, sevPillClass } from "../lib/util";
import { RefreshNotices, SlowNotice } from "./queryViewKit";

const PAGE_LIMIT = 50;

export function Proyectos() {
  const { data: recipe } = useRecipe();
  const role = useSession()?.organization.role;
  const proyectosQuery = useCarteraProyectos({ limit: PAGE_LIMIT });
  const { data } = proyectosQuery;
  // loading / error / disabled / ready come from the contract
  // (useQueryView), never from `isLoading || !data`.
  const view = useQueryView(proyectosQuery);
  const [q, setQ] = useState("");
  const [extraPages, setExtraPages] = useState<CarteraDashboardGroupItem[][]>([]);
  // undefined = "no page loaded via loadMore yet, defer to `data`'s own
  // cursor"; null = "confirmed no more pages" — keyset pagination, no
  // total-page count exists to drive a numbered Pager.
  const [manualCursor, setManualCursor] = useState<string | null | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);

  const allItems = useMemo(
    () => [...(data?.items ?? []), ...extraPages.flat()],
    [data, extraPages]
  );

  const rankByKey = useMemo(
    () => new Map(recipe?.severities.map((s) => [s.rank, s]) ?? []),
    [recipe]
  );

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return allItems
      .filter((p) => {
        if (!ql) return true;
        const hay = `${p.name} ${p.subline ?? ""}`.toLowerCase();
        return hay.includes(ql);
      })
      .sort((a, b) => {
        const rankDiff = (b.maxOpenSeverityRank ?? -1) - (a.maxOpenSeverityRank ?? -1);
        if (rankDiff !== 0) return rankDiff;
        const obsDiff = b.openObservations - a.openObservations;
        if (obsDiff !== 0) return obsDiff;
        return a.name.localeCompare(b.name, "es");
      });
  }, [allItems, q]);

  const effectiveNextCursor =
    manualCursor !== undefined ? manualCursor : (data?.nextCursor ?? null);

  async function loadMore() {
    if (!effectiveNextCursor) return;
    setLoadingMore(true);
    try {
      const page = await carteraDashboardApi.groups({
        limit: PAGE_LIMIT,
        cursor: effectiveNextCursor,
      });
      setExtraPages((prev) => [...prev, page.items]);
      setManualCursor(page.nextCursor);
    } finally {
      setLoadingMore(false);
    }
  }

  const hasMore = effectiveNextCursor !== null;

  const groupPlural = recipe?.labels.assetGroupPlural ?? "Proyectos";
  const groupLabel = recipe?.labels.assetGroup ?? "Proyecto";
  const assetPlural = data?.labels.assetPlural ?? recipe?.labels.assetPlural ?? "Unidades";
  /** Los números de postventa se detectan por PRESENCIA del campo, no
   *  por slug de vertical: el servidor sólo los manda cuando la receta
   *  declara ciclo de observaciones, y ausente significa «esto no aplica
   *  acá», no «cero». Con ellos la pantalla es la grilla de cards del
   *  panel v2; sin ellos (facility, arriendos) sigue siendo la tabla
   *  semáforo. Se mira la lista completa y no la filtrada para que una
   *  búsqueda sin coincidencias no cambie de forma. */
  const tienePostventa = allItems.some((p) => p.unassignedObservations !== undefined);

  const count = allItems.length;
  const countLabel = `${count} ${(count === 1 ? groupLabel : groupPlural).toLowerCase()}`;
  const resultados = `${filtered.length} ${filtered.length === 1 ? "resultado" : "resultados"}`;

  // The frame (header) lands at once on every branch; only the content
  // below it skeletonizes, errors or fills (handoff §11).
  const header = (
    <div className="page-header">
      <div className="page-header__lead">
        <div className="page-header__eyebrow">Cartera{data ? ` · ${countLabel}` : ""}</div>
        <h1 className="page-header__title">{groupPlural}</h1>
        <p className="page-header__sub">
          {tienePostventa || !data
            ? `Activación de ${assetPlural.toLowerCase()} y observaciones que esperan una acción de la inmobiliaria, por ${groupLabel.toLowerCase()}.`
            : `Estado semáforo de cada ${groupLabel.toLowerCase()}, ${assetPlural.toLowerCase()} y observaciones abiertas. Click en una fila para ver el detalle.`}
        </p>
      </div>
      {canUsePortfolioAnalysis(role, recipe) && (
        <div className="page-header__actions">
          <Link to="/analisis" className="btn btn--secondary btn--sm">
            <Icon.Sparkles s={14} /> Análisis con IA
          </Link>
        </div>
      )}
    </div>
  );

  if (view.kind === "error") {
    return (
      <div className="page">
        {header}
        <div className="panel">
          {view.offline ? (
            <PageError
              title="Sin conexión"
              description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
              onRetry={view.retry}
            />
          ) : (
            <PageError
              title={`No pudimos cargar tus ${groupPlural.toLowerCase()}`}
              description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
              onRetry={view.retry}
            />
          )}
        </div>
      </div>
    );
  }
  if (view.kind === "loading") {
    // Dos cards en blanco con la silueta real (README §Skeletons): nombre
    // 70 % × 14, dirección 50 % × 10, pill 92 × 22, dos celdas y el pie.
    return (
      <div className="page">
        {header}
        <PageSkeleton label={`Cargando ${groupPlural.toLowerCase()}…`} visible={view.visible}>
          <div className="proj-grid">
            {[0, 1].map((i) => (
              <div key={i} className="proj-card" aria-hidden="true">
                <div className="proj-card__head">
                  <div className="proj-card__lead skel-stack">
                    <Skel width="70%" height={14} />
                    <Skel width="50%" height={10} />
                  </div>
                  <Skel width={92} height={22} pill />
                </div>
                <div className="proj-card__body">
                  <div className="proj-card__cell">
                    <Skel width="60%" height={9} />
                    <Skel width="40%" height={22} />
                    <Skel width="100%" height={6} pill />
                  </div>
                  <div className="proj-card__cell">
                    <Skel width="70%" height={9} />
                    <Skel width="24%" height={22} />
                    <Skel width="85%" height={10} />
                  </div>
                </div>
                <div className="proj-card__foot">
                  <Skel width="60%" height={10} />
                </div>
              </div>
            ))}
          </div>
        </PageSkeleton>
        {view.slow && <SlowNotice onRetry={() => void proyectosQuery.refetch()} />}
      </div>
    );
  }
  // Disabled never happens for this query (it has no `enabled` gate),
  // but if it did nothing is on its way: the empty state, never an
  // endless skeleton.
  if (view.kind !== "ready") {
    return (
      <div className="page">
        {header}
        <div className="panel">
          <EmptyState title={`Aún no tienes ${groupPlural.toLowerCase()}`} />
        </div>
      </div>
    );
  }

  const notices = (
    <RefreshNotices
      refreshFailed={view.refreshFailed}
      revalidatingHint={view.revalidatingHint}
      onRetry={() => void proyectosQuery.refetch()}
    />
  );

  const pager = hasMore && (
    <div className="pager">
      <span className="pager__meta" aria-live="polite">
        Mostrando {allItems.length} {groupPlural.toLowerCase()}
      </span>
      <button
        type="button"
        className="pager__btn"
        onClick={() => void loadMore()}
        disabled={loadingMore}
      >
        {loadingMore ? "Cargando…" : "Cargar más"}
      </button>
    </div>
  );

  const vacio = (
    <EmptyState
      title={
        allItems.length === 0 ? `Aún no tienes ${groupPlural.toLowerCase()}` : "Sin coincidencias"
      }
      description={
        allItems.length === 0
          ? `Los ${groupPlural.toLowerCase()} aparecen aquí cuando se crea el primero en cartera.`
          : "Ajusta la búsqueda."
      }
    />
  );

  if (tienePostventa) {
    return (
      <div className="page">
        {header}
        {notices}

        {/* La barra va suelta sobre la grilla — sin fondo ni borde
            (README §1) — así que no hereda el marco de `.filters`
            dentro de un panel. */}
        <div
          className="filters"
          style={{ padding: 0, background: "transparent", border: 0, marginBottom: 16 }}
        >
          <div className="filter-search">
            <Icon.Search />
            <input
              placeholder={`Buscar ${groupLabel.toLowerCase()}…`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="filter-spacer" />
          <span className="filter-sort">{resultados}</span>
        </div>

        {filtered.length === 0 ? (
          <div className="panel">{vacio}</div>
        ) : (
          <div className="proj-grid">
            {filtered.map((p) => (
              <ProjCard key={p.id} p={p} />
            ))}
          </div>
        )}
        {pager}
      </div>
    );
  }

  return (
    <div className="page">
      {header}
      {notices}

      <div className="panel">
        <div className="filters">
          <div className="filter-search">
            <Icon.Search />
            <input
              placeholder={`Buscar ${groupLabel.toLowerCase()}…`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="filter-spacer" />
          <span className="filter-sort">{resultados}</span>
        </div>

        <div className="panel__body--flush">
          {filtered.length === 0 ? (
            vacio
          ) : (
            <table className="dtable">
              <caption className="sr-only">Listado de {groupPlural.toLowerCase()}</caption>
              <thead>
                <tr>
                  <th scope="col">{groupLabel}</th>
                  <th scope="col" className="col-num">
                    {assetPlural}
                  </th>
                  <th scope="col" className="col-num">
                    Abiertas
                  </th>
                  <th scope="col">Última actividad</th>
                  <th scope="col">Estado</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <ProyectoRow
                    key={p.id}
                    p={p}
                    severity={
                      p.maxOpenSeverityRank !== null
                        ? rankByKey.get(p.maxOpenSeverityRank)
                        : undefined
                    }
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
        {pager}
      </div>
    </div>
  );
}

/** La fila semáforo de una vertical SIN ciclo de observaciones (facility,
 *  arriendos). Postventa no pasa por acá: usa ProjCard. */
function ProyectoRow({
  p,
  severity,
}: {
  p: CarteraDashboardGroupItem;
  severity: { key: string; label: string; rank: number } | undefined;
}) {
  const navigate = useNavigate();

  function handleRowClick(e: React.MouseEvent<HTMLTableRowElement>) {
    if ((e.target as HTMLElement).closest('a, button, [role="button"]')) return;
    navigate(`/proyectos/${p.id}`);
  }

  const actividad = p.lastObservationAt ?? p.lastActivityAt;

  return (
    <tr className="row-link" onClick={handleRowClick}>
      <td>
        <div className="cell-with-sub">
          <Link to={`/proyectos/${p.id}`} className="cell-strong cell-strong--link">
            {p.name}
          </Link>
          <span className="cell-sub">{p.subline ?? "—"}</span>
        </div>
      </td>
      <td className="col-num">
        {/* «48 de 120 unidades activas» — la activación es la invitación
            al propietario, así que el total solo no dice nada sobre
            cuántas pueden recibir observaciones. */}
        {p.activeAssetCount !== undefined ? (
          <span>
            {p.activeAssetCount} <span className="cell-sub">de {p.assetCount}</span>
          </span>
        ) : (
          p.assetCount
        )}
      </td>
      <td className="col-num">
        {p.openObservations > 0 ? (
          <span className="st-pill st-pill--warn">{p.openObservations} abiertas</span>
        ) : (
          0
        )}
      </td>
      <td className="cell-meta">
        {actividad ? `${fmtDate(actividad)} · ${lastSeenLabel(actividad)}` : "Sin actividad"}
      </td>
      <td>
        {severity ? (
          <span className={`st-pill ${sevPillClass(severity.key)}`}>{sevLabel(severity.key)}</span>
        ) : (
          // fitness-allow: postventa-labels-single-source — rollup de proyecto sin severidad abierta, no es el estado de una observación
          <span className="st-pill st-pill--ok">Conforme</span>
        )}
      </td>
      <td>
        <Link
          to={`/proyectos/${p.id}`}
          className="row-chevron"
          aria-label={`Abrir detalle de ${p.name}`}
        >
          <Icon.ChevronRight />
        </Link>
      </td>
    </tr>
  );
}
